import AsyncSelect, { AsyncProps } from "react-select/async";
import { GroupBase } from "react-select";
import { Control, Controller, FieldValues, Path, RegisterOptions } from "react-hook-form";

type AsyncSelectFieldProps<Option, TFieldValues extends FieldValues = FieldValues> = Omit<
  AsyncProps<Option, false, GroupBase<Option>>,
  "name" | "value" | "onChange"
> & {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
};

export const AsyncSelectField = <Option, TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  rules,
  styles,
  ...props
}: AsyncSelectFieldProps<Option, TFieldValues>) => {
  const menuPortalTarget = props.menuPortalTarget ?? (typeof document !== "undefined" ? document.body : undefined);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const inErrorState = Boolean(fieldState.error);
        const errorStyles: AsyncProps<Option, false, GroupBase<Option>>["styles"] = {
          control: (base, state) => ({
            ...base,
            minHeight: "36px",
            fontSize: "0.875rem",
            backgroundColor: inErrorState ? "#FEF2F2" : "#F9FAFB",
            borderColor: inErrorState ? "#EF4444" : state.isFocused ? base.borderColor : base.borderColor,
            boxShadow: inErrorState && state.isFocused ? "0 0 0 1px #EF4444" : base.boxShadow,
            "&:hover": {
              borderColor: inErrorState ? "#EF4444" : "#9CA3AF",
            },
            ...((styles?.control?.(base, state) as object) ?? {}),
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
            pointerEvents: "auto" as const,
            ...((styles?.menuPortal?.(base, {} as never) as object) ?? {}),
          }),
        };

        return (
          <AsyncSelect<Option>
            {...props}
            name={field.name}
            value={(field.value as Option | null) ?? null}
            onChange={(opt) => field.onChange(opt ?? null)}
            onBlur={field.onBlur}
            menuPortalTarget={menuPortalTarget}
            menuPosition={props.menuPosition ?? "fixed"}
            styles={errorStyles}
          />
        );
      }}
    />
  );
};
